import * as React from "react";
import {
  Column,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import {
  StyledSearchBar,
  StyledSelectbox,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTableStyles";
import { isNullOrEmpty } from "Utils/StringUtils";
import { FilterSearchIcon } from "Components/Shared/Icons/FilterSearchIcon";
import { FilterIcon } from "Components/Shared/Icons/FilterIcon";
import { MenuItem, Select } from "@material-ui/core";
import _, { Dictionary } from "lodash";
import { FilterMultiplyIcon } from "Components/Shared/Icons/FilterMultiplyIcon";
import { PrimaryDataTableSearch } from "Components/Shared/PrimaryDataTable/PrimaryDataTableSearch";

type Props<T> = {
  index: number;
  column: Column<T>;
  selectedFilter: TableIDType<T> | null;
  setSelectedFilter: (value: TableIDType<T> | null) => void;
  isOpenedFilter: boolean;
  setIsOpenedFilter: (value: boolean) => void;
  wrapperFilterRefs: React.MutableRefObject<(HTMLElement | null)[]>;
};

export type FilterProps = {
  setSearchInput?: (value: string) => void;
  searchInput?: string;
  setSelectboxValue?: (value: string) => void;
  selectboxValue?: string;
  selectboxListItems?: Dictionary<string>;
};

export const PrimaryDataTableFilter = <T extends object>(props: Props<T>) => {
  const {
    index,
    column,
    selectedFilter,
    isOpenedFilter,
    wrapperFilterRefs,
    setSelectedFilter,
    setIsOpenedFilter,
  } = props;

  const [openSelect, setOpenSelect] = React.useState(false);
  const [isEscKeyPress, setIsEscKeyPress] = React.useState(false);

  let timeout: number;

  React.useEffect(() => {
    if (openSelect) {
      handleSelectPopup();
    }
    // eslint-disable-next-line
  }, [openSelect]);

  React.useEffect(() => {
    if (isEscKeyPress && column.filter?.setSearchInput) {
      setIsOpenedFilter(false);
    }
    setIsEscKeyPress(false);
    // eslint-disable-next-line
  }, [isEscKeyPress]);

  const handleCloseSelectbox = () => setOpenSelect(false);

  const handleOpenSelectbox = () => setOpenSelect(true);

  const onClickFilter = () => {
    if (selectedFilter === column.id || selectedFilter === null) {
      setIsOpenedFilter(!isOpenedFilter);
    } else {
      setIsOpenedFilter(true);
    }
    setSelectedFilter(column.id);
  };

  const handleChangeSelectbox = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    if (column.filter && column.filter.setSelectboxValue) {
      column.filter.setSelectboxValue(event.target.value as string);
    }
  };

  const [searchInputValue, setSearchInputValue] = React.useState("");
  const [wasClickedSearchBtn, setWasClickedSearchBtn] = React.useState(false);

  React.useEffect(() => {
    const isDiffSearchValue = searchInputValue !== column.filter?.searchInput;
    if (column.filter && column.filter.setSearchInput && isDiffSearchValue) {
      column.filter.setSearchInput(searchInputValue);
      setIsOpenedFilter(false);
    }
    // eslint-disable-next-line
  }, [searchInputValue]);

  const onClickSearch = (isSearchBtn: boolean) => {
    if (column.filter && column.filter.setSearchInput) {
      if (isSearchBtn) {
        setWasClickedSearchBtn(!wasClickedSearchBtn);
      } else {
        column.filter.setSearchInput("");
        setSearchInputValue("");
      }
      setIsOpenedFilter(false);
    }
  };

  const setFilteredCssClass = () =>
    isNullOrEmpty(column.filter?.searchInput) &&
    (isNullOrEmpty(column.filter?.selectboxValue) ||
      column.filter?.selectboxValue === "none")
      ? ""
      : "filtered-by";

  const isOpenFilter = () => isOpenedFilter && selectedFilter === column.id;

  const setOpenedCssClass = () => (isOpenFilter() ? "opened" : "closed");

  const isShownFilter = () =>
    column.filter?.setSearchInput || column.filter?.setSelectboxValue;

  const handleSelectPopup = () => {
    clearTimeout(timeout);
    removeStyle();

    const wrapper = getPopoverWrapper();
    if (wrapper) {
      const { top, left } = wrapper.style;
      createStyle(top, left);
      return;
    }
    timeout = setTimeout(() => handleSelectPopup(), 100);
  };

  const getPopoverWrapper = () => {
    const popoverRoot = document.getElementsByClassName("ibis-MuiPopover-root");
    const paperRoot =
      popoverRoot.length > 0
        ? popoverRoot[0].getElementsByClassName("ibis-MuiPaper-root")
        : null;

    return paperRoot && paperRoot.length > 0
      ? (paperRoot[0] as HTMLElement)
      : null;
  };

  const createStyle = (top: string, left: string) => {
    const styleWrapper = document.createElement("div");
    styleWrapper.id = "MuiPopover-style";

    const styleElm = document.createElement("style");
    styleWrapper.appendChild(styleElm);
    const text = document.createTextNode(
      `.ibis-MuiPopover-root .ibis-MuiPaper-root {top:${top} !important; left:${left} !important}`,
    );
    styleElm.appendChild(text);

    document.body.appendChild(styleWrapper);
  };

  const removeStyle = () => {
    const popoverStyle = document.getElementById("MuiPopover-style");
    if (popoverStyle) {
      popoverStyle.remove();
    }
  };

  return (
    <>
      {isShownFilter() && (
        <>
          <div
            ref={el => (wrapperFilterRefs.current[index] = el)}
            className={`filter-popup ${setOpenedCssClass()}`}
          >
            {column.filter?.setSearchInput && (
              <>
                <StyledSearchBar>
                  <PrimaryDataTableSearch
                    searchValue={column.filter?.searchInput}
                    onSearch={setSearchInputValue}
                    isFocused={isOpenFilter()}
                    onKeyPressEsc={setIsEscKeyPress}
                    wasClickedSearchBtn={wasClickedSearchBtn}
                  />
                </StyledSearchBar>
                <div
                  className={
                    isNullOrEmpty(column.filter?.searchInput)
                      ? "filter-search-icon-wrapper"
                      : "cancel-filter-wrapper"
                  }
                  onClick={() =>
                    onClickSearch(isNullOrEmpty(column.filter?.searchInput))
                  }
                >
                  {isNullOrEmpty(column.filter?.searchInput) ? (
                    <FilterSearchIcon className={"filter-search-icon"} />
                  ) : (
                    <FilterMultiplyIcon className={"cancel-filter"} />
                  )}
                </div>
              </>
            )}

            {column.filter?.setSelectboxValue && (
              <StyledSelectbox className={"selectbox"}>
                <Select
                  id={`select-${index}`}
                  value={column.filter.selectboxValue}
                  onChange={handleChangeSelectbox}
                  open={openSelect}
                  onClose={handleCloseSelectbox}
                  onOpen={handleOpenSelectbox}
                >
                  {_.map(
                    column.filter.selectboxListItems,
                    (value, key) =>
                      column.filter?.selectboxListItems && (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ),
                  )}
                </Select>
              </StyledSelectbox>
            )}
          </div>

          <div
            onClick={() => onClickFilter()}
            className={`filter-wrapper ${setFilteredCssClass()} ${setOpenedCssClass()}`}
          >
            <div className={"filter-icon-wrapper"}>
              <FilterIcon />
            </div>
          </div>
        </>
      )}
    </>
  );
};
